import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { readsCustomerRecord, readsOverviewRecord } from '../lib/navigation.js';
import { needsPermissionCheck } from '../lib/permissions-freshness.js';

/**
 * The customer scope of the workbench (portal workbench, 2026-09-29): a
 * layout route, no longer a tab container. It loads the customer record once
 * per customer for the users who may read it (operators and the customer's
 * own admins) and renders the section route below it
 * (pages/CustomerSections.jsx). A developer or qlik-credentials holder gets
 * no record (GET /customers/{id} refuses them too), only their sections. A
 * viewer (read-only Overview and Security) loads the narrowed record those two
 * pages need, and never runs the permissions check (a write it is refused).
 */
export default function CustomerDetail() {
  const { customerId } = useParams();
  const shell = useOutletContext();
  const { navClaims } = shell;
  const fullAccess = readsCustomerRecord(navClaims, customerId);
  const readsRecord = readsOverviewRecord(navClaims, customerId);

  const [loaded, setLoaded] = useState({ customerId: null, record: null, error: null });
  const [reloads, setReloads] = useState(0);
  // Reload without clearing what is on screen (no loading flash after a save).
  const refresh = useCallback(() => setReloads((n) => n + 1), []);

  useEffect(() => {
    if (!readsRecord) return undefined;
    let cancelled = false;
    api.getCustomer(customerId)
      .then((record) => {
        if (!cancelled) setLoaded({ customerId, record, error: null });
      })
      .catch((e) => {
        if (!cancelled) {
          setLoaded((prev) => ({ customerId, record: prev.customerId === customerId ? prev.record : null, error: e.message }));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, readsRecord, reloads]);

  const isCurrent = loaded.customerId === customerId;
  const record = isCurrent ? loaded.record : null;
  const recordError = isCurrent ? loaded.error : null;
  const customer = record?.customer;
  const permissionsCheckedAt = record?.customer?.permissionsCheckedAt;

  // ENTRAID-315: a deployed customer is never checked by Onboarding, so verify
  // the granted Graph permissions once per page session when the stored check is
  // missing or stale. Silent on any failure (including 429): no banner, no retry.
  const checkedRef = useRef(new Set());
  useEffect(() => {
    if (!fullAccess || !customer || checkedRef.current.has(customerId)) return;
    if (!needsPermissionCheck(customer)) return;
    checkedRef.current.add(customerId);
    api.verifyConsent(customerId)
      .then((answer) => {
        if (answer?.verified === true || answer?.state) refresh();
      })
      .catch((e) => console.debug('permissions check skipped', e?.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, permissionsCheckedAt, fullAccess, customer?.status]);

  // ENTRAID-347 redesign: no customer header here. The customer's name and status live in the top bar switcher (which
  // also holds the operator's "View as client"), and each section page owns its one h1 (components/PageHeader.jsx).
  return (
    <div>
      {/* Keyed by customer: switching customers remounts the section, so a slow
          answer for the previous customer can never land on the next one. */}
      <Outlet key={customerId} context={{ ...shell, customerId, fullAccess, record, recordError, refresh }} />
    </div>
  );
}
